import { User } from "firebase/auth";
import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid, Cell,
} from "recharts";
import { useBreadcrumbs } from "../context/BreadcrumbContext";
import {
  getGwChallengePicks,
  saveGwChallengePicks,
  GwChallengePickRow,
} from "../api/gwChallenge";
import { fetchLeague, fetchLeagueFixtures, LeagueGwFixtures } from "../api/leagues";
import { fetchPlayers, fetchClubs } from "../api/players";
import PageLoader from "../components/PageLoader";
import ClubBadge from "../components/ClubBadge";
import CustomSelect from "../components/CustomSelect";
import { getClubBadge } from "../data/clubBadges";
import StatusDot from "../components/StatusDot";
import { useExportImage } from "../hooks/useExportImage";
import { safeFilename } from "../utils/shareLeague";
import { card, widePageContainer, secondaryBtn, inputStyle, smallBtn, exportBtn, panelHeading } from "../styles";
import StatBox from "../components/league/StatBox";
import ModeIconSmall from "../components/league/ModeIconSmall";
import { MODE_CONTENT, MODE_COLOR_VAR } from "./createLeague/modeContent";
import { groupByDate, FixtureRow } from "../components/league/fixtureUtils";
import { useIsMobile } from "../hooks/useIsMobile";

const PAGE_SIZE = 25;

interface Props { user: User; }

interface PickerPlayer {
  id: string;
  name: string;
  club: string;
  position: string | null;
  nationality: string | null;
  goals?: number;
}

const tooltipStyle = {
  background: "var(--color-surface)",
  border: "1px solid var(--color-border)",
  borderRadius: 8,
  fontSize: "0.78rem",
};

function BarTooltip({ active, payload }: any) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload as { name: string; goals: number; player: string };
  return (
    <div style={{ ...tooltipStyle, padding: "0.4rem 0.7rem" }}>
      <div style={{ fontWeight: 700, color: "var(--color-text)" }}>{d.name}</div>
      {d.player && <div style={{ color: "var(--color-text-muted)", fontSize: "0.7rem" }}>{d.player}</div>}
      <div style={{ color: d.goals > 0 ? "var(--color-success)" : "var(--color-text-dim)" }}>
        {d.goals} {d.goals === 1 ? "goal" : "goals"}
      </div>
    </div>
  );
}

export default function GwChallengePage({ user }: Props) {
  const { id: leagueId } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [leagueName, setLeagueName] = useState("");
  const [leagueStatus, setLeagueStatus] = useState<"PREPARING" | "RUNNING" | "COMPLETED" | null>(null);
  const [leagueSeason, setLeagueSeason] = useState("");
  const [competitionId, setCompetitionId] = useState("");
  const [rows, setRows] = useState<GwChallengePickRow[]>([]);
  const [totalPoints, setTotalPoints] = useState(0);
  const [rank, setRank] = useState<number | null>(null);
  const [memberCount, setMemberCount] = useState(0);
  const [selectedGwId, setSelectedGwId] = useState<string | null>(null);
  const [, setSaving] = useState<Record<string, boolean>>({});

  const [shareDropdownOpen, setShareDropdownOpen] = useState(false);
  const { status: exportStatus, loading: shareLoading, runExport } = useExportImage();
  const exportHeaderRef   = useRef<HTMLDivElement>(null);
  const exportTimelineRef = useRef<HTMLDivElement>(null);

  const [selectedGwFixtures, setSelectedGwFixtures] = useState<LeagueGwFixtures | null>(null);

  const [poolPlayers, setPoolPlayers] = useState<PickerPlayer[]>([]);
  const [poolTotal, setPoolTotal] = useState(0);
  const [poolPage, setPoolPage] = useState(1);
  const [poolLoading, setPoolLoading] = useState(false);
  const [poolSearch, setPoolSearch] = useState("");
  const [poolClub, setPoolClub] = useState("");
  const [poolPosition, setPoolPosition] = useState("");
  const [clubs, setClubs] = useState<string[]>([]);
  const poolDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [activeTab, setActiveTab] = useState<"selections" | "performance">("selections");
  const [fixturesOpen, setFixturesOpen] = useState(true);
  const [chartTab, setChartTab] = useState<"per-gw" | "cumulative">("per-gw");
  const chipRefs = useRef<(HTMLButtonElement | null)[]>([]);

  useBreadcrumbs(
    leagueName
      ? [{ label: t("common.home"), to: "/overview" }, { label: leagueName, to: `/leagues/${leagueId}` }, { label: "My Selections" }]
      : [{ label: t("common.home"), to: "/overview" }],
  );

  const load = () => {
    if (!leagueId) return;
    setLoading(true);
    Promise.all([fetchLeague(user, leagueId), getGwChallengePicks(user, leagueId)])
      .then(([league, picksData]) => {
        setLeagueName(league.name);
        setLeagueStatus((league as any).status ?? null);
        setLeagueSeason((league as any).competition?.season ?? "");
        const compId = (league as any).competition?.id ?? "";
        setCompetitionId(compId);
        setRows(picksData.rows);
        setTotalPoints(picksData.totalPoints);
        setRank(picksData.rank ?? null);
        setMemberCount(picksData.memberCount ?? 0);
        const firstPickable = picksData.rows.find((r) => !r.completed && !r.locked);
        setSelectedGwId(firstPickable?.gameweekId ?? picksData.rows[0]?.gameweekId ?? null);
        if (compId) fetchClubs(user, compId).then(setClubs).catch(() => {});
      })
      .catch(() => setError(t("common.somethingWentWrong")))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [leagueId]); // eslint-disable-line

  const GW_ROW_HEIGHT = 56;

  // Keep export timeline scrolled to current GW
  useEffect(() => {
    if (!rows.length || !exportTimelineRef.current) return;
    const currentIdx = rows.findIndex((r) => !r.completed);
    const targetIdx = Math.max(0, currentIdx - 2);
    exportTimelineRef.current.scrollTop = targetIdx * GW_ROW_HEIGHT;
  }, [rows]); // eslint-disable-line

  // Scroll active chip into view
  useEffect(() => {
    if (!rows.length) return;
    const idx = rows.findIndex((r) => r.gameweekId === selectedGwId);
    if (idx >= 0 && chipRefs.current[idx]) {
      chipRefs.current[idx]?.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
    }
  }, [selectedGwId, rows]);

  useEffect(() => {
    if (!leagueId || !selectedGwId) { setSelectedGwFixtures(null); return; }
    fetchLeagueFixtures(user, leagueId, selectedGwId).then(setSelectedGwFixtures).catch(() => setSelectedGwFixtures(null));
  }, [leagueId, selectedGwId]); // eslint-disable-line

  // Mobile: auto-open fixtures when GW has a pick
  useEffect(() => {
    if (isMobile) {
      const row = rows.find((r) => r.gameweekId === selectedGwId);
      setFixturesOpen(!!row?.pick);
    }
  }, [selectedGwId]); // eslint-disable-line

  const fetchPool = useCallback(async (search: string, club: string, position: string, page: number) => {
    if (!competitionId) return;
    setPoolLoading(true);
    try {
      const result = await fetchPlayers(user, {
        competitionId, page, pageSize: PAGE_SIZE, sort: "goals",
        search: search || undefined,
        club: club || undefined,
        position: position || undefined,
      });
      setPoolPlayers(result.players as PickerPlayer[]);
      setPoolTotal(result.total);
    } catch { /* ignore */ } finally { setPoolLoading(false); }
  }, [competitionId, user]);

  useEffect(() => {
    if (!competitionId) return;
    if (poolDebounce.current) clearTimeout(poolDebounce.current);
    poolDebounce.current = setTimeout(() => { setPoolPage(1); fetchPool(poolSearch, poolClub, poolPosition, 1); }, 250);
  }, [poolSearch, poolClub, poolPosition, competitionId]); // eslint-disable-line

  useEffect(() => { if (competitionId) fetchPool(poolSearch, poolClub, poolPosition, poolPage); }, [poolPage]); // eslint-disable-line

  // Derived
  const usedMap: Record<string, string> = {};
  const usedIdMap: Record<string, string> = {};
  rows.forEach((r) => { if (r.pick) { usedMap[r.pick.playerId] = r.gameweekName; usedIdMap[r.pick.playerId] = r.gameweekId; } });

  const selectedRow = rows.find((r) => r.gameweekId === selectedGwId) ?? null;
  const canPickSelected = selectedRow ? !selectedRow.completed && !selectedRow.locked : false;
  const currentPickId = selectedRow?.pick?.playerId ?? null;
  const pickedCount = rows.filter((r) => r.pick !== null).length;
  const completedRows = rows.filter((r) => r.completed);
  const hitRows = completedRows.filter((r) => (r.pick?.goals ?? 0) > 0);
  const hitRate = completedRows.length > 0 ? Math.round((hitRows.length / completedRows.length) * 100) : null;
  const totalPages = Math.ceil(poolTotal / PAGE_SIZE);
  const selectedIdx = rows.findIndex((r) => r.gameweekId === selectedGwId);
  const navPrev = () => { if (selectedIdx > 0) setSelectedGwId(rows[selectedIdx - 1].gameweekId); };
  const navNext = () => { if (selectedIdx < rows.length - 1) setSelectedGwId(rows[selectedIdx + 1].gameweekId); };

  // Fixture-based derived state
  const fixtureClubs = new Set<string>(
    selectedGwFixtures?.gameweek.fixtures.flatMap((f) => [f.homeClub.name, f.awayClub.name]) ?? []
  );
  const hasNoFixture = (club: string) =>
    selectedGwFixtures !== null && fixtureClubs.size > 0 && !fixtureClubs.has(club);
  const pickClubName = selectedRow?.pick?.player.club ?? null;
  // Warn on pick if fixtures loaded but club absent — includes the case where GW has no fixtures at all
  const pickHasNoFixture = pickClubName !== null && selectedGwFixtures !== null && !fixtureClubs.has(pickClubName);

  // GW context panel derived
  const [statusLabel, statusColor] = selectedRow
    ? selectedRow.completed
      ? ["Completed", "var(--color-success)"]
      : selectedRow.locked
      ? ["In Progress", "oklch(0.88 0.20 92)"]
      : ["Open", "var(--color-warning)"]
    : ["", ""];

  const pickFixture = pickClubName
    ? selectedGwFixtures?.gameweek.fixtures.find(
        (f) => f.homeClub.name === pickClubName || f.awayClub.name === pickClubName
      ) ?? null
    : null;
  const fixtureLabel = pickFixture
    ? pickFixture.status === "FINISHED"
      ? `${pickFixture.homeClub.shortName ?? pickFixture.homeClub.name} ${pickFixture.homeScore ?? 0}–${pickFixture.awayScore ?? 0} ${pickFixture.awayClub.shortName ?? pickFixture.awayClub.name}`
      : `${pickFixture.homeClub.shortName ?? pickFixture.homeClub.name} vs ${pickFixture.awayClub.shortName ?? pickFixture.awayClub.name}${pickFixture.kickoffTime ? ` · ${new Date(pickFixture.kickoffTime).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/London" })}` : ""}`
    : null;
  const pickCfg = selectedRow?.pick ? getClubBadge(selectedRow.pick.player.club) : null;
  const pickPts = selectedRow?.pick?.goals ?? null;

  // Performance stats
  const avgPerGw = completedRows.length > 0 ? totalPoints / completedRows.length : null;
  let currentStreak = 0;
  for (let i = completedRows.length - 1; i >= 0; i--) {
    if ((completedRows[i].pick?.goals ?? 0) > 0) currentStreak++;
    else break;
  }

  // Chart data
  const perGwData = completedRows.map((r) => ({
    name: r.gameweekName,
    goals: r.pick?.goals ?? 0,
    player: r.pick?.player.name ?? "",
  }));
  let cumGoals = 0;
  const cumulativeData = [
    { name: "Start", goals: 0 },
    ...completedRows.map((r) => {
      cumGoals += r.pick?.goals ?? 0;
      return { name: r.gameweekName, goals: cumGoals };
    }),
  ];
  const tickInterval = perGwData.length > 8 ? Math.floor(perGwData.length / 5) : 0;

  const handlePick = async (player: PickerPlayer) => {
    if (!leagueId || !selectedGwId) return;
    setSaving((p) => ({ ...p, [selectedGwId]: true }));
    try {
      await saveGwChallengePicks(user, leagueId, [{ gameweekId: selectedGwId, playerId: player.id }]);
      setRows((prev) => prev.map((r) =>
        r.gameweekId === selectedGwId
          ? { ...r, pick: { playerId: player.id, player: { id: player.id, name: player.name, club: player.club, nationality: null }, goals: null } }
          : r
      ));
    } catch { /* non-fatal */ } finally { setSaving((p) => ({ ...p, [selectedGwId]: false })); }
  };

  const handleRemove = async (gwId: string) => {
    if (!leagueId) return;
    setSaving((p) => ({ ...p, [gwId]: true }));
    try {
      await saveGwChallengePicks(user, leagueId, [{ gameweekId: gwId, playerId: null }]);
      setRows((prev) => prev.map((r) => r.gameweekId === gwId ? { ...r, pick: null } : r));
    } catch { /* non-fatal */ } finally { setSaving((p) => ({ ...p, [gwId]: false })); }
  };

  const handleMovePlayer = async (playerId: string) => {
    if (!leagueId || !selectedGwId) return;
    const fromGwId = usedIdMap[playerId];
    if (!fromGwId) return;
    const sourceRow = rows.find((r) => r.gameweekId === fromGwId);
    const playerData = sourceRow?.pick?.player;
    setSaving((p) => ({ ...p, [selectedGwId]: true }));
    try {
      await saveGwChallengePicks(user, leagueId, [
        { gameweekId: fromGwId, playerId: null },
        { gameweekId: selectedGwId, playerId },
      ]);
      setRows((prev) => prev.map((r) => {
        if (r.gameweekId === fromGwId) return { ...r, pick: null };
        if (r.gameweekId === selectedGwId && playerData) return { ...r, pick: { playerId, player: playerData, goals: null } };
        return r;
      }));
    } catch { /* non-fatal */ } finally { setSaving((p) => ({ ...p, [selectedGwId]: false })); }
  };

  if (loading) return <PageLoader />;
  if (error) return <div style={{ padding: "2rem", color: "var(--color-danger)" }}>{error}</div>;

  const handleExport = async (mode: "simple" | "full", action: "download" | "copy") => {
    setShareDropdownOpen(false);
    const els = mode === "full" ? [exportHeaderRef.current, exportTimelineRef.current] : [exportTimelineRef.current];
    await runExport(els, action, `${safeFilename(leagueName)}.png`);
  };

  const pillBtn = (active: boolean): React.CSSProperties => ({
    padding: "3px 10px", borderRadius: 99, fontSize: "0.72rem", fontWeight: 600,
    cursor: "pointer", fontFamily: "var(--font-body)",
    border: active ? "1px solid var(--color-accent)" : "1px solid var(--color-border)",
    background: active ? "var(--color-accent-glow)" : "transparent",
    color: active ? "var(--color-accent)" : "var(--color-text-muted)",
  });

  const gwChipStyle = (row: GwChallengePickRow, isSelected: boolean): React.CSSProperties => {
    const hasPick = !!row.pick;
    let bg = "transparent";
    let border = "1px solid var(--color-border-subtle)";
    if (isSelected) {
      bg = "var(--color-accent-glow)";
      border = "2px solid var(--color-accent)";
    } else if (row.completed && hasPick) {
      bg = "color-mix(in srgb, var(--color-success) 8%, transparent)";
    } else if (row.completed && !hasPick) {
      border = "1px dashed var(--color-border)";
    } else if (!hasPick && !row.locked) {
      bg = "color-mix(in srgb, var(--color-warning) 8%, transparent)";
      border = "1px solid var(--color-warning)";
    }
    return {
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      gap: "0.15rem", padding: "0.4rem 0.6rem", minWidth: 72, height: 52,
      borderRadius: 10, flexShrink: 0, background: bg, border,
      cursor: "pointer", fontFamily: "var(--font-body)", transition: "background 0.12s, border 0.12s",
    };
  };

  return (
    <>
    <style>{`.gw-nav::-webkit-scrollbar{display:none}`}</style>
    <div className="sg-fade-in" style={{ ...widePageContainer, padding: "1.5rem 0" }}>

      {/* ── Header card ── */}
      <div ref={exportHeaderRef} style={{ ...card, marginBottom: "1.25rem" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.5rem", flexWrap: "wrap" }}>
          {[
            <span key="mode" style={{ display: "inline-flex", alignItems: "center", gap: "0.3rem" }}>
              <ModeIconSmall mode="GAMEWEEK_CHALLENGE" color={MODE_COLOR_VAR.GAMEWEEK_CHALLENGE} />
              <span style={{ fontSize: "0.62rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: MODE_COLOR_VAR.GAMEWEEK_CHALLENGE }}>
                {t(`${MODE_CONTENT.GAMEWEEK_CHALLENGE.keyRoot}.label`)}
              </span>
            </span>,
            leagueSeason,
          ].filter(Boolean).map((s, i) => (
            <React.Fragment key={i}>
              {typeof s === "string"
                ? <span style={{ fontSize: "0.62rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--color-text-dim)" }}>{s}</span>
                : s}
              <span style={{ color: "var(--color-border)", fontSize: "0.75rem" }}>·</span>
            </React.Fragment>
          ))}
          {leagueStatus && <StatusDot status={leagueStatus} />}
        </div>

        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "1rem", marginBottom: "0.4rem", flexWrap: "wrap" }}>
          <div style={{ minWidth: 0 }}>
            <h1 style={{ margin: "0 0 0.25rem", fontFamily: "var(--font-display)", fontSize: "1.75rem", fontWeight: 800, color: "var(--color-text)" }}>
              {user.displayName ? `${user.displayName}${user.displayName.endsWith("s") ? "'" : "'s"} Selections` : "My Selections"}
            </h1>
            <p style={{ margin: 0, fontSize: "0.82rem", color: "var(--color-text-muted)" }}>
              <strong style={{ color: "var(--color-accent)" }}>One player</strong> per gameweek · each player can only be selected <strong style={{ color: "var(--color-accent)" }}>once</strong>
            </p>
          </div>
          <div data-no-export style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexShrink: 0 }}>
            {rows.length > 0 && (
              <div style={{ position: "relative" }}>
                <button style={exportBtn} onClick={() => setShareDropdownOpen((v) => !v)} disabled={shareLoading}>
                  <svg width="13" height="13" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 2v11M6 9l4 4 4-4"/><path d="M3 14v2a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-2"/></svg>
                  Export
                </button>
                {shareDropdownOpen && (
                  <>
                    <div style={{ position: "fixed", inset: 0, zIndex: 99 }} onClick={() => setShareDropdownOpen(false)} />
                    <div style={{ position: "absolute", right: 0, top: "calc(100% + 6px)", background: "var(--color-surface)", border: "1px solid var(--color-border)", borderRadius: 10, boxShadow: "0 8px 32px var(--color-shadow)", zIndex: 100, minWidth: 190, overflow: "hidden" }}>
                      <div style={{ padding: "0.35rem 1rem 0.2rem", fontSize: "0.6rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--color-text-dim)" }}>Simple</div>
                      {(["download", "copy"] as const).map((action) => (
                        <button key={action} onClick={() => handleExport("simple", action)} style={{ display: "block", width: "100%", padding: "0.5rem 1rem", background: "none", border: "none", textAlign: "left", fontSize: "0.85rem", color: "var(--color-text)", cursor: "pointer" }}>
                          {action === "download" ? "Download image" : "Copy as image"}
                        </button>
                      ))}
                      <div style={{ margin: "0.2rem 1rem", borderTop: "1px solid var(--color-border-subtle)" }} />
                      <div style={{ padding: "0.35rem 1rem 0.2rem", fontSize: "0.6rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--color-text-dim)" }}>Full</div>
                      {(["download", "copy"] as const).map((action) => (
                        <button key={action} onClick={() => handleExport("full", action)} style={{ display: "block", width: "100%", padding: "0.5rem 1rem", background: "none", border: "none", textAlign: "left", fontSize: "0.85rem", color: "var(--color-text)", cursor: "pointer" }}>
                          {action === "download" ? "Download image" : "Copy as image"}
                        </button>
                      ))}
                    </div>
                  </>
                )}
                {exportStatus && (
                  <div style={{ position: "absolute", right: 0, top: "calc(100% + 6px)", zIndex: 100, whiteSpace: "nowrap", padding: "0.4rem 0.7rem", borderRadius: 8, fontSize: "0.78rem", fontWeight: 600, background: "var(--color-surface)", border: `1px solid ${exportStatus.type === "error" ? "var(--color-danger)" : "var(--color-border)"}`, color: exportStatus.type === "error" ? "var(--color-danger)" : "var(--color-text)", boxShadow: "0 8px 32px var(--color-shadow)" }}>
                    {exportStatus.message}
                  </div>
                )}
              </div>
            )}
            <Link to={`/leagues/${leagueId}`} style={{ ...secondaryBtn, textDecoration: "none" }}>← League</Link>
          </div>
        </div>

        <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", marginTop: "1rem" }}>
          <StatBox label="Score" value={totalPoints} sub="total goals" accent borderColor="var(--color-accent)" />
          <StatBox label="Selections" value={`${pickedCount}/${rows.length}`} sub="gameweeks picked" mobileHide borderColor="var(--color-accent)" />
          {hitRate !== null && <StatBox label="Hit rate" value={`${hitRate}%`} sub="GWs with a goal" borderColor="var(--color-accent)" />}
          {avgPerGw !== null && <StatBox label="Avg / GW" value={avgPerGw.toFixed(1)} sub="goals per gameweek" borderColor="var(--color-accent)" />}
          {currentStreak > 0 && <StatBox label="Streak" value={currentStreak} sub="consecutive GWs" mobileHide borderColor="var(--color-accent)" />}
          {rank !== null && memberCount > 0 && <StatBox label="Rank" value={`#${rank}`} sub={`of ${memberCount}`} mobileHide borderColor="var(--color-accent)" />}
        </div>
      </div>

      {/* ── Missing selection banner ── */}
      {(() => {
        const firstOpen = rows.find((r) => !r.completed && !r.locked);
        const upcoming = firstOpen && !firstOpen.pick ? firstOpen : null;
        if (!upcoming) return null;
        return (
          <div style={{
            background: "color-mix(in srgb, var(--color-warning) 12%, transparent)",
            border: "1px solid var(--color-warning)",
            borderRadius: 8, padding: "0.75rem 1.25rem", marginBottom: "1.25rem",
            fontSize: "0.85rem", color: "var(--color-warning)", fontWeight: 500,
            display: "flex", alignItems: "center", gap: "0.6rem",
          }}>
            ⚠ No player selected for upcoming gameweek {upcoming.gameweekName}.{" "}
            <button
              onClick={() => { setActiveTab("selections"); setSelectedGwId(upcoming.gameweekId); }}
              style={{ background: "none", border: "none", cursor: "pointer", color: "inherit", fontWeight: 700, fontFamily: "var(--font-body)", fontSize: "inherit", padding: 0 }}
            >
              Make your selection now
            </button>
          </div>
        );
      })()}

      {/* ── Tab bar ── */}
      <div style={{ display: "flex", marginBottom: "1.25rem", border: "1px solid var(--color-border)", borderRadius: 10, overflow: "hidden" }}>
        {(["selections", "performance"] as const).map((tab, idx) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              flex: 1, padding: "0.65rem 1rem", borderRadius: 0, fontSize: "0.85rem",
              fontWeight: activeTab === tab ? 700 : 500, cursor: "pointer", fontFamily: "var(--font-body)",
              border: "none", borderRight: idx === 0 ? "1px solid var(--color-border)" : "none",
              background: activeTab === tab ? "var(--color-accent)" : "transparent",
              color: activeTab === tab ? "var(--color-btn-primary-text)" : "var(--color-text-muted)",
              transition: "background 0.15s, color 0.15s",
            }}
          >
            {tab === "selections" ? "Selections" : "Performance"}
          </button>
        ))}
      </div>

      {/* ── SELECTIONS TAB ── */}
      {activeTab === "selections" && (
        <>
          {/* GW Navigator chip strip */}
          {rows.length > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "1.25rem" }}>
              <button
                onClick={navPrev}
                disabled={selectedIdx <= 0}
                style={{ flexShrink: 0, width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 8, border: "1px solid var(--color-border)", background: "transparent", cursor: selectedIdx <= 0 ? "default" : "pointer", color: selectedIdx <= 0 ? "var(--color-border)" : "var(--color-text-muted)", fontFamily: "var(--font-body)", fontSize: "0.9rem" }}
                aria-label="Previous gameweek"
              >‹</button>
              <div
                className="gw-nav"
                style={{
                  flex: 1, display: "flex", gap: "0.5rem", overflowX: "auto", scrollbarWidth: "none", padding: "0.25rem 0.125rem",
                  // Edge fade so the strip reads as horizontally scrollable even with the
                  // scrollbar hidden — the ‹/› buttons are a fallback, not the primary hint.
                  WebkitMaskImage: "linear-gradient(to right, transparent 0, black 12px, black calc(100% - 12px), transparent 100%)",
                  maskImage: "linear-gradient(to right, transparent 0, black 12px, black calc(100% - 12px), transparent 100%)",
                }}
              >
              {rows.map((row, idx) => {
                const isSelected = row.gameweekId === selectedGwId;
                const dotColor = row.completed
                  ? "var(--color-success)"
                  : row.locked
                  ? "oklch(0.88 0.20 92)"
                  : row.pick
                  ? "var(--color-accent)"
                  : "var(--color-warning)";
                const nameParts = (row.pick?.player.name ?? "").split(" ");
                const surname = nameParts[nameParts.length - 1];
                const displayName = surname.length > 10 ? surname.slice(0, 9) + "…" : surname;
                return (
                  <button
                    key={row.gameweekId}
                    ref={(el) => { chipRefs.current[idx] = el; }}
                    onClick={() => setSelectedGwId(row.gameweekId)}
                    style={gwChipStyle(row, isSelected)}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "0.25rem" }}>
                      <span style={{ fontFamily: "var(--font-display)", fontSize: "0.7rem", fontWeight: 800, color: isSelected ? "var(--color-accent)" : "var(--color-text)", lineHeight: 1 }}>
                        {row.gameweekName}
                      </span>
                      <span style={{ width: 5, height: 5, borderRadius: "50%", flexShrink: 0, background: dotColor }} />
                    </div>
                    <div style={{ fontSize: "0.58rem", color: isSelected ? "var(--color-accent)" : "var(--color-text-muted)", maxWidth: 68, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textAlign: "center", lineHeight: 1.3 }}>
                      {displayName || "–"}
                    </div>
                  </button>
                );
              })}
              </div>
              <button
                onClick={navNext}
                disabled={selectedIdx >= rows.length - 1}
                style={{ flexShrink: 0, width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 8, border: "1px solid var(--color-border)", background: "transparent", cursor: selectedIdx >= rows.length - 1 ? "default" : "pointer", color: selectedIdx >= rows.length - 1 ? "var(--color-border)" : "var(--color-text-muted)", fontFamily: "var(--font-body)", fontSize: "0.9rem" }}
                aria-label="Next gameweek"
              >›</button>
            </div>
          )}

          {/* Hidden off-screen vertical timeline for export */}
          <div ref={exportTimelineRef} style={{ position: "absolute", left: -9999, top: 0, width: 320, overflowX: "hidden", overflowY: "auto", maxHeight: GW_ROW_HEIGHT * 8 }}>
            {rows.map((row, idx) => {
              const pts = row.pick?.goals ?? null;
              const cfg = row.pick ? getClubBadge(row.pick.player.club) : null;
              return (
                <div key={row.gameweekId} style={{ display: "grid", gridTemplateColumns: "48px 1fr auto", alignItems: "center", gap: "0.75rem", padding: "0.75rem 1rem", borderBottom: idx < rows.length - 1 ? "1px solid var(--color-border-subtle)" : undefined }}>
                  <div style={{ fontFamily: "var(--font-display)", fontSize: "1rem", fontWeight: 800, color: "var(--color-text-dim)" }}>{row.gameweekName}</div>
                  <div>
                    {row.pick ? (
                      <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                        {cfg && <ClubBadge config={cfg} size={16} title={row.pick.player.club} />}
                        <span style={{ fontSize: "0.82rem", fontWeight: 600, color: "var(--color-text)" }}>{row.pick.player.name}</span>
                      </div>
                    ) : (
                      <span style={{ fontSize: "0.82rem", color: "var(--color-text-dim)" }}>–</span>
                    )}
                  </div>
                  <div style={{ textAlign: "right", minWidth: 44 }}>
                    {row.completed && pts !== null ? (
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                        <div style={{ fontFamily: "var(--font-display)", fontSize: "1.2rem", fontWeight: 700, color: pts > 0 ? "var(--color-success)" : "var(--color-text-muted)", lineHeight: 1 }}>{pts}</div>
                        <div style={{ fontSize: "0.55rem", fontWeight: 700, textTransform: "uppercase", color: "var(--color-text-dim)", letterSpacing: "0.06em" }}>PTS</div>
                      </div>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Two-column grid: GW Context | Player Pool */}
          <div style={{ display: "grid", gridTemplateColumns: (!isMobile && leagueStatus !== "COMPLETED") ? "380px 1fr" : "1fr", gap: "1.25rem", alignItems: "start" }}>

            {/* GW Context Panel */}
            <div style={{ ...card, padding: 0, overflow: "hidden" }}>
              {/* Pick header */}
              <div style={{ padding: "0.85rem 1.25rem", borderBottom: "1px solid var(--color-border)", background: "var(--color-surface-alt)" }}>
                {selectedRow ? (
                  <>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.6rem" }}>
                      <span style={{ fontFamily: "var(--font-display)", fontSize: "1.3rem", fontWeight: 800, color: "var(--color-text)" }}>
                        {selectedRow.gameweekName}
                      </span>
                      {statusLabel && (
                        <span style={{ display: "inline-block", fontSize: "0.55rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", padding: "2px 7px", borderRadius: 99, background: `color-mix(in srgb, ${statusColor} 15%, transparent)`, border: `1px solid ${statusColor}`, color: statusColor }}>
                          {statusLabel}
                        </span>
                      )}
                    </div>
                    {selectedRow.pick ? (
                      <>
                        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                          {pickCfg && <ClubBadge config={pickCfg} size={28} title={selectedRow.pick.player.club} />}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <span style={{ fontSize: "0.95rem", fontWeight: 700, color: "var(--color-accent)", lineHeight: 1.2 }}>{selectedRow.pick.player.name}</span>
                            <div style={{ fontSize: "0.72rem", color: "var(--color-text-muted)" }}>{selectedRow.pick.player.club}</div>
                            {fixtureLabel && <div style={{ fontSize: "0.65rem", color: "var(--color-text-dim)", marginTop: "0.1rem" }}>{fixtureLabel}</div>}
                          </div>
                          {selectedRow.completed && pickPts !== null && (
                            <div style={{ textAlign: "right", flexShrink: 0 }}>
                              <div style={{ fontFamily: "var(--font-display)", fontSize: "1.4rem", fontWeight: 800, color: pickPts > 0 ? "var(--color-success)" : "var(--color-text-muted)", lineHeight: 1 }}>{pickPts}</div>
                              <div style={{ fontSize: "0.55rem", fontWeight: 700, textTransform: "uppercase", color: "var(--color-text-dim)", letterSpacing: "0.06em" }}>PTS</div>
                            </div>
                          )}
                          {canPickSelected && (
                            <button onClick={() => handleRemove(selectedRow.gameweekId)} style={{ fontSize: "0.72rem", background: "none", border: "1px solid var(--color-danger)", borderRadius: 6, padding: "0.3rem 0.65rem", color: "var(--color-danger)", cursor: "pointer", fontFamily: "var(--font-body)", flexShrink: 0 }}>
                              Remove
                            </button>
                          )}
                        </div>
                        {pickHasNoFixture && (
                          <div style={{ marginTop: "0.6rem", padding: "0.5rem 0.75rem", borderRadius: 6, background: "color-mix(in srgb, var(--color-warning) 12%, transparent)", border: "1px solid var(--color-warning)", fontSize: "0.72rem", color: "var(--color-warning)", fontWeight: 500 }}>
                            ⚠ {selectedRow.pick.player.club} have no fixture in {selectedRow.gameweekName} — consider changing your pick
                          </div>
                        )}
                      </>
                    ) : (
                      <span style={{ fontSize: "0.82rem", color: "var(--color-text-dim)", fontStyle: "italic" }}>
                        {canPickSelected ? "No player selected — choose from the player pool" : "No player selected for this gameweek."}
                      </span>
                    )}
                  </>
                ) : (
                  <span style={{ fontSize: "0.82rem", color: "var(--color-text-dim)" }}>Select a gameweek to get started.</span>
                )}
              </div>

              {/* Fixture list */}
              {(() => {
                const fixtures = selectedGwFixtures?.gameweek.fixtures ?? [];
                const groups = groupByDate(fixtures);
                return (
                  <>
                    <div style={{ padding: "0.45rem 1rem 0.35rem", background: "var(--color-surface-alt)", borderBottom: "1px solid var(--color-border-subtle)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <span style={{ fontSize: "0.55rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--color-text-dim)" }}>
                        Fixtures
                      </span>
                      {isMobile && (
                        <button onClick={() => setFixturesOpen((v) => !v)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: "0.7rem", color: "var(--color-text-muted)", fontFamily: "var(--font-body)", padding: 0 }}>
                          {fixturesOpen ? "▲ Hide" : "▼ Show"}
                        </button>
                      )}
                    </div>
                    {(!isMobile || fixturesOpen) && (
                      <div style={{ maxHeight: 320, overflowY: "auto" }}>
                        {groups.length === 0 ? (
                          <div style={{ padding: "0.75rem 1rem", fontSize: "0.72rem", color: "var(--color-text-dim)", fontStyle: "italic" }}>
                            {selectedGwFixtures ? "No fixtures scheduled" : "Loading…"}
                          </div>
                        ) : (
                          groups.map((g) => (
                            <div key={g.key}>
                              <div style={{ fontSize: "0.55rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--color-text-dim)", padding: "0.3rem 0.9rem 0.15rem", background: "var(--color-surface-alt)", borderBottom: "1px solid var(--color-border-subtle)" }}>
                                {g.label}
                              </div>
                              {g.fixtures.map((f) => {
                                const isHighlighted = pickClubName !== null &&
                                  (f.homeClub.name === pickClubName || f.awayClub.name === pickClubName);
                                return (
                                  <div key={f.id} style={isHighlighted
                                    ? { background: "var(--color-accent-glow)", borderLeft: "3px solid var(--color-accent)" }
                                    : { borderLeft: "3px solid transparent" }
                                  }>
                                    <FixtureRow fixture={f} />
                                  </div>
                                );
                              })}
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>

            {/* Player Pool — hidden when completed */}
            {leagueStatus !== "COMPLETED" && (
              <div style={isMobile ? {} : { position: "sticky", top: 108 }}>
                <div style={{ ...card, padding: 0, overflow: "hidden" }}>
                  {/* Search */}
                  <div style={{ padding: "0.6rem 1.25rem", borderBottom: "1px solid var(--color-border-subtle)" }}>
                    <div style={{ position: "relative" }}>
                      <span style={{ position: "absolute", left: "0.6rem", top: "50%", transform: "translateY(-50%)", fontSize: "0.85rem", color: "var(--color-accent)", pointerEvents: "none" }}>⌕</span>
                      <input
                        style={{ ...inputStyle, fontSize: "0.85rem", paddingLeft: "1.8rem", width: "100%", border: "1px solid var(--color-accent)", background: "color-mix(in srgb, var(--color-accent) 6%, var(--color-input-bg))" }}
                        placeholder="Search players…"
                        value={poolSearch}
                        onChange={(e) => setPoolSearch(e.target.value)}
                      />
                    </div>
                  </div>

                  {/* Filters */}
                  <div style={{ padding: "0.6rem 1.25rem", borderBottom: "1px solid var(--color-border-subtle)", display: "flex", alignItems: "center", gap: "1.25rem", flexWrap: "wrap" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                      <span style={{ fontSize: "0.6rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "var(--color-text-dim)" }}>Pos</span>
                      {(["", "GK", "DF", "MF", "FW"] as const).map((pos) => (
                        <button key={pos || "all"} onClick={() => setPoolPosition(pos)} style={pillBtn(poolPosition === pos)}>{pos || "All"}</button>
                      ))}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                      <span style={{ fontSize: "0.6rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "var(--color-text-dim)" }}>Club</span>
                      <div style={{ display: "inline-flex", borderRadius: 99, border: "1px solid var(--color-accent)", overflow: "hidden" }}>
                        <CustomSelect
                          value={poolClub}
                          onChange={(v) => setPoolClub(v)}
                          options={clubs.map((c) => ({ value: c, label: c }))}
                          placeholder="All clubs"
                          style={{ border: "none", fontSize: "0.72rem", padding: "3px 8px", fontWeight: 600, borderRadius: 99 }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Table header */}
                  <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr auto auto" : "1fr 1fr auto auto auto", gap: "0.75rem", padding: "0.5rem 1.25rem", borderBottom: "1px solid var(--color-border-subtle)" }}>
                    <span style={{ fontSize: "0.6rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "var(--color-text-dim)" }}>Player</span>
                    {!isMobile && <span style={{ fontSize: "0.6rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "var(--color-text-dim)" }}>Club</span>}
                    {!isMobile && <span style={{ fontSize: "0.6rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "var(--color-text-dim)" }}>Pos</span>}
                    <span style={{ fontSize: "0.6rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "var(--color-text-dim)", textAlign: "right" }}>Goals</span>
                    <span style={isMobile ? { width: 60 } : { width: 80 }} />
                  </div>

                  {/* Player rows */}
                  <div style={{ maxHeight: "calc(100vh - 480px)", overflowY: "auto", opacity: poolLoading ? 0.4 : 1, transition: "opacity 0.15s" }}>
                    {poolPlayers.map((p) => {
                      const isCurrentPick = p.id === currentPickId;
                      const usedGw = usedMap[p.id];
                      const isUsedElsewhere = !!(usedGw && p.id !== currentPickId);
                      const cfg = getClubBadge(p.club);
                      const noFixture = hasNoFixture(p.club);

                      return (
                        <div
                          key={p.id}
                          style={{
                            display: "grid",
                            gridTemplateColumns: isMobile ? "1fr auto auto" : "1fr 1fr auto auto auto",
                            gap: "0.75rem",
                            alignItems: "center",
                            padding: "0.6rem 1.25rem",
                            borderBottom: "1px solid var(--color-border-subtle)",
                            background: isCurrentPick ? "var(--color-accent-glow)" : isUsedElsewhere ? "color-mix(in srgb, var(--color-danger) 6%, transparent)" : undefined,
                          }}
                        >
                          <div style={{ minWidth: 0 }}>
                            <span style={{ display: "block", fontSize: "0.85rem", fontWeight: 600, color: isUsedElsewhere ? "var(--color-text-muted)" : "var(--color-accent)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {p.name}
                              {noFixture && (
                                <span title={`${p.club} have no fixture in ${selectedRow?.gameweekName ?? "this GW"}`} style={{ color: "var(--color-warning)", marginLeft: "0.3rem", fontSize: "0.75rem" }}>⚠</span>
                              )}
                            </span>
                            {isMobile && (
                              <div style={{ display: "flex", alignItems: "center", gap: "0.3rem", marginTop: 2 }}>
                                {cfg && <ClubBadge config={cfg} size={12} title={p.club} />}
                                <span style={{ fontSize: "0.68rem", color: "var(--color-text-dim)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.club}</span>
                              </div>
                            )}
                          </div>

                          {!isMobile && (
                            <div style={{ display: "flex", alignItems: "center", gap: "0.35rem", minWidth: 0 }}>
                              {cfg && <ClubBadge config={cfg} size={16} title={p.club} />}
                              <span style={{ fontSize: "0.82rem", color: "var(--color-text-muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.club}</span>
                            </div>
                          )}

                          {!isMobile && (
                            <span style={{ fontSize: "0.65rem", fontWeight: 700, textTransform: "uppercase", padding: "2px 5px", borderRadius: 4, background: "var(--color-surface-alt)", color: "var(--color-text-muted)", border: "1px solid var(--color-border-subtle)" }}>
                              {p.position ?? "—"}
                            </span>
                          )}

                          <span style={{ fontSize: "0.85rem", fontWeight: (p.goals ?? 0) > 0 ? 700 : 400, color: (p.goals ?? 0) > 0 ? "var(--color-text)" : "var(--color-text-dim)", textAlign: "right", minWidth: 28 }}>
                            {(p.goals ?? 0) > 0 ? p.goals : "—"}
                          </span>

                          <div style={{ width: isMobile ? 60 : 80, display: "flex", justifyContent: "flex-end" }}>
                            {isCurrentPick && canPickSelected ? (
                              <button onClick={() => handleRemove(selectedGwId!)} style={{ fontSize: "0.7rem", width: isMobile ? 54 : 72, background: "none", border: "1px solid var(--color-danger)", borderRadius: 6, padding: "0.28rem 0.4rem", color: "var(--color-danger)", cursor: "pointer", fontFamily: "var(--font-body)" }}>Remove</button>
                            ) : isUsedElsewhere ? (
                              canPickSelected ? (
                                <button onClick={() => handleMovePlayer(p.id)} style={{ fontSize: "0.7rem", width: isMobile ? 54 : 72, background: "var(--color-accent)", border: "none", borderRadius: 6, padding: "0.28rem 0.4rem", color: "var(--color-btn-primary-text)", cursor: "pointer", fontFamily: "var(--font-body)", fontWeight: 600 }}>Move</button>
                              ) : (
                                <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: isMobile ? 54 : 72, fontSize: "0.6rem", fontWeight: 700, color: "var(--color-text-dim)", whiteSpace: "nowrap" }}>
                                  USED · {usedGw}
                                </span>
                              )
                            ) : canPickSelected ? (
                              <button onClick={() => handlePick(p)} style={{ fontSize: "0.7rem", width: isMobile ? 54 : 72, background: "var(--color-accent)", border: "none", borderRadius: 6, padding: "0.28rem 0.4rem", color: "var(--color-btn-primary-text)", cursor: "pointer", fontFamily: "var(--font-body)", fontWeight: 600 }}>Add</button>
                            ) : null}
                          </div>
                        </div>
                      );
                    })}
                    {poolPlayers.length === 0 && !poolLoading && (
                      <div style={{ padding: "2rem", textAlign: "center", color: "var(--color-text-muted)", fontSize: "0.85rem" }}>No players found.</div>
                    )}
                  </div>

                  {/* Pool pagination */}
                  {totalPages > 1 && (
                    <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", padding: "0.6rem 1.25rem", borderTop: "1px solid var(--color-border)" }}>
                      <button style={smallBtn} disabled={poolPage <= 1} onClick={() => setPoolPage((p) => p - 1)}>Previous</button>
                      <span style={{ fontSize: "0.82rem", color: "var(--color-text-muted)", flex: 1, textAlign: "center" }}>{poolPage} / {totalPages}</span>
                      <button style={smallBtn} disabled={poolPage >= totalPages} onClick={() => setPoolPage((p) => p + 1)}>Next</button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* ── PERFORMANCE TAB ── */}
      {activeTab === "performance" && (
        <div>
          {isMobile && (
            <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", marginBottom: "1rem" }}>
              <StatBox label="Selections" value={`${pickedCount}/${rows.length}`} sub="gameweeks picked" borderColor="var(--color-accent)" />
              {currentStreak > 0 && <StatBox label="Streak" value={currentStreak} sub="consecutive GWs" borderColor="var(--color-accent)" />}
              {rank !== null && memberCount > 0 && <StatBox label="Rank" value={`#${rank}`} sub={`of ${memberCount}`} borderColor="var(--color-accent)" />}
            </div>
          )}
          {completedRows.length === 0 ? (
            <div style={{ ...card, padding: "2rem", textAlign: "center", color: "var(--color-text-muted)", fontSize: "0.85rem", fontStyle: "italic" }}>
              No data yet — check back after the first gameweek completes.
            </div>
          ) : (
            <>
              {/* Chart card */}
              <div style={{ ...card, padding: "1rem 1.25rem" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.75rem" }}>
                  <h2 style={{ ...panelHeading, margin: 0 }}>Score Progression</h2>
                  <div style={{ display: "flex", gap: "0.25rem" }}>
                    {(["per-gw", "cumulative"] as const).map((tab) => (
                      <button
                        key={tab}
                        onClick={() => setChartTab(tab)}
                        style={{
                          padding: "0.2rem 0.6rem", fontSize: "0.75rem", borderRadius: 6, border: "none",
                          cursor: "pointer", fontFamily: "var(--font-body)",
                          background: chartTab === tab ? "var(--color-accent)" : "var(--color-surface-alt)",
                          color: chartTab === tab ? "var(--color-btn-primary-text)" : "var(--color-text-muted)",
                        }}
                      >
                        {tab === "per-gw" ? "Per GW" : "Cumulative"}
                      </button>
                    ))}
                  </div>
                </div>

                {chartTab === "per-gw" ? (
                  <ResponsiveContainer width="100%" height={180}>
                    <BarChart data={perGwData} margin={{ top: 4, right: 4, bottom: 4, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border-subtle)" vertical={false} />
                      <XAxis dataKey="name" tick={{ fontSize: 9, fill: "var(--color-text-dim)" }} axisLine={false} tickLine={false} interval={tickInterval} />
                      <YAxis tick={{ fontSize: 9, fill: "var(--color-text-dim)" }} axisLine={false} tickLine={false} domain={[0, "auto"]} allowDecimals={false} />
                      <Tooltip content={<BarTooltip />} cursor={false} />
                      <Bar dataKey="goals" radius={[2, 2, 0, 0]} minPointSize={3}>
                        {perGwData.map((entry, index) => (
                          <Cell key={index} fill={entry.goals > 0 ? "var(--color-accent)" : "var(--color-border)"} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <ResponsiveContainer width="100%" height={180}>
                    <AreaChart data={cumulativeData} margin={{ top: 4, right: 4, bottom: 4, left: 0 }}>
                      <defs>
                        <linearGradient id="gwc-area-grad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="var(--color-accent)" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="var(--color-accent)" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border-subtle)" />
                      <XAxis dataKey="name" tick={{ fontSize: 9, fill: "var(--color-text-dim)" }} axisLine={false} tickLine={false} interval={cumulativeData.length > 9 ? Math.floor(cumulativeData.length / 5) : 0} />
                      <YAxis tick={{ fontSize: 9, fill: "var(--color-text-dim)" }} axisLine={false} tickLine={false} domain={[0, "auto"]} allowDecimals={false} />
                      <Tooltip contentStyle={tooltipStyle} formatter={(value: any) => [`${value} goals`, "Total"]} />
                      <Area type="monotone" dataKey="goals" stroke="var(--color-accent)" strokeWidth={2} fill="url(#gwc-area-grad)" dot={false} />
                    </AreaChart>
                  </ResponsiveContainer>
                )}
              </div>
            </>
          )}
        </div>
      )}

    </div>
    </>
  );
}
